import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Table from 'components/ui/Table';
import BiodataDetailsView from 'components/BiodataDetailsView';
import { useAuth } from 'context/AuthContext';
import { employerAPI, biodataAPI } from 'utils/api';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const EmployerSubmittedApplicationDetails = () => {
  const navigate = useNavigate();
  const { vdrId } = useParams();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');
  const [detailError, setDetailError] = useState(null);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeBiodata, setActiveBiodata] = useState(null);

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  const loadDetails = useCallback(async () => {
    if (!vdrId) return;
    try {
      setLoadingDetails(true);
      setDetailError(null);
      setApplicationDetails(null);
      setWorkers([]);

      const result = await employerAPI.getSelections(vdrId);
      const app = result?.application || null;
      const allWorkers = result?.workers || [];

      setApplicationDetails(app);
      // Only show workers approved for this application by default
      setWorkers(allWorkers.filter(w => (w.status || '').toLowerCase() === 'approved'));
    } catch (err) {
      console.error('Failed to load application details', err);
      setDetailError(err.response?.data?.error || err.message || 'Failed to load application details');
    } finally {
      setLoadingDetails(false);
    }
  }, [vdrId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const handleViewWorker = useCallback(async (worker) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    setActiveBiodata(null);
    try {
      const result = await biodataAPI.getBiodataById(worker.biodata_id);
      if (result.status === 'success' && result.data) {
        setActiveBiodata(result.data);
      } else {
        setActiveBiodata({ error: result.message || 'Failed to load biodata details.' });
      }
    } catch (err) {
      console.error('Failed to fetch biodata details', err);
      setActiveBiodata({ error: err.response?.data?.error || err.message || 'Failed to load biodata details.' });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setActiveBiodata(null);
    setDetailLoading(false);
  };

  const displayedWorkers = useMemo(() => {
    const term = workerSearch.trim().toLowerCase();
    if (!term) return workers;
    return workers.filter(w => {
      const name = w?.biodata?.full_name || '';
      const passport = w?.biodata?.passport_no || '';
      return name.toLowerCase().includes(term) || passport.toLowerCase().includes(term);
    })
  }, [workers, workerSearch]);

  const handleSidebarToggle = () => setSidebarCollapsed(prev => !prev);

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || 'Employer'}
      />

      <div className="flex pt-16">
        <RoleBasedSidebar collapsed={sidebarCollapsed} activePath="/employer-submitted-applications" />

        <main className="flex-1 p-6">
          <BreadcrumbNavigation
            items={[
              { label: 'Home', href: '/employer-dashboard' },
              { label: 'Submitted Applications', href: '/employer-submitted-applications' },
              { label: vdrId }
            ]}
          />

          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Application {vdrId}</h1>
              <p className="text-sm text-gray-600 mt-1">Submitted application details and worker tracking.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/employer-submitted-applications')}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium flex items-center"
              >
                <Icon name="ArrowLeft" size={14} className="mr-1" />
                Back to list
              </button>
              <button
                onClick={() => navigate(`/employer-vdr-applications/${vdrId}`)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium flex items-center"
              >
                <Icon name="Eye" size={14} className="mr-1" />
                View full VDR
              </button>
            </div>
          </div>

          {/* Summary */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            {loadingDetails && (
              <div className="p-4 text-sm text-gray-500">Loading application details…</div>
            )}
            {detailError && (
              <div className="p-4 text-sm text-red-600">{String(detailError)}</div>
            )}
            {!loadingDetails && applicationDetails && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-gray-500">Reference</div>
                  <div className="text-gray-900 font-medium">{applicationDetails.reference_number || `APP-${applicationDetails.id}`}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Approval Status</div>
                  <div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      (applicationDetails.status || '').toLowerCase() === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {(applicationDetails.status || '').toUpperCase() || '—'}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Approved At</div>
                  <div className="text-gray-900 font-medium">{formatDateTime(applicationDetails.approved_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">FCL Progress</div>
                  <div className="text-gray-900 font-medium">{applicationDetails.fcl_count || 0} FCL • {applicationDetails.approved_count || 0} approved</div>
                </div>
              </div>
            )}
          </section>

          {/* Workers full-width */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Selected Workers</h2>
              <div className="relative w-64">
                <Icon name="Search" size={16} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  placeholder="Search by name or passport"
                  className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="p-4">
              <Table
                columns={[
                  {
                    label: 'Worker',
                    render: (worker) => (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                          {worker?.biodata?.photo_url ? (
                            <Image src={worker.biodata.photo_url} alt={worker?.biodata?.full_name} className="w-full h-full object-cover" />
                          ) : (
                            <Icon name="User" size={18} className="text-gray-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{worker?.biodata?.full_name || '—'}</div>
                          <div className="text-xs text-gray-500">Passport: {worker?.biodata?.passport_no || '—'}</div>
                          <div className="text-xs text-gray-500 mt-1">{worker?.biodata?.nationality || '—'}</div>
                        </div>
                      </div>
                    )
                  },
                  {
                    label: 'Status',
                    render: () => (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Approved</span>
                    )
                  },
                  {
                    label: 'Timeline',
                    render: (worker) => (
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>Approved: {formatDateTime(worker?.approved_at)}</div>
                        <div>Updated: {formatDateTime(worker?.updated_at)}</div>
                      </div>
                    )
                  },
                ]}
                data={displayedWorkers}
                renderActions={(worker) => (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewWorker(worker)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium flex items-center justify-center"
                    >
                      <Icon name="Eye" size={14} className="mr-1" />
                      View details
                    </button>
                  </div>
                )}
                emptyState={<div className="text-sm text-gray-500">No approved workers found for this application.</div>}
              />
            </div>
          </section>

          {/* Biodata detail modal */}
          {detailModalOpen && (
            <>
              <style>{`
                @media print {
                  .biodata-modal-overlay {
                    display: none !important;
                  }
                  .biodata-modal-content {
                    position: static !important;
                    max-width: 100% !important;
                    max-height: none !important;
                    box-shadow: none !important;
                  }
                }
              `}</style>
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 biodata-modal-overlay">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto biodata-modal-content">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Worker Details</h3>
                    <button onClick={closeDetailModal} className="text-gray-500 hover:text-gray-700">
                      <Icon name="X" size={18} />
                    </button>
                  </div>
                  <div className="p-4">
                    {detailLoading && <div className="text-sm text-gray-500">Loading biodata…</div>}
                    {!detailLoading && activeBiodata && !activeBiodata.error && (
                      <BiodataDetailsView biodata={activeBiodata} />
                    )}
                    {!detailLoading && activeBiodata?.error && (
                      <div className="text-sm text-red-600">{String(activeBiodata.error)}</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default EmployerSubmittedApplicationDetails;
